import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTheme } from "@mui/material";
import React from "react";
import { ChartName, ChartTypesMap, DefaultDate, SubMenuOption } from "./analyticsTypes";
import DropdownSubmenu from "./DropdownSubmenu";
import { FilterButton, FilterButtonWrapper } from "./styles/filter.styles";

interface GlobalFilterProps {
  globalDate: string;
  showChartSelector: boolean;
  charts: ChartTypesMap;
  onDateClick: (newDate: DefaultDate) => void;
  onChartClick: (chartName: ChartName) => void;
}

const GlobalFilter: React.FC<GlobalFilterProps> = ({
  globalDate,
  showChartSelector,
  charts,
  onDateClick,
  onChartClick,
}) => {
  const theme = useTheme();

  const [period, setPeriod] = React.useState<string>(globalDate);
  const [dateAnchorEl, setDateAnchorEl] = React.useState<HTMLElement | null>(null);
  const openDate = Boolean(dateAnchorEl);

  const [addChartAnchorEl, setAddChartAnchorEl] = React.useState<HTMLElement | null>(null);
  const openAddChart = Boolean(addChartAnchorEl);

  // Called when user selects a date from the dropdown menu
  const handleDateSelection = (selection: SubMenuOption) => {
    setPeriod(selection.label);
    onDateClick(selection.value as DefaultDate);
    handleDateClose();
  };

  // Opens the date picker dropdown when date button is clicked
  const handleDateClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent event bubbling
    setDateAnchorEl(event.currentTarget);
  };

  // Closes the date picker dropdown
  const handleDateClose = () => {
    setDateAnchorEl(null);
  };

  // Called when user selects a chart from the add chart dropdown
  const handleAddChartSelection = (selection: SubMenuOption) => {
    onChartClick(selection.value as ChartName);
    handleAddChartClose();
  };

  // Opens the add chart dropdown when button is clicked
  const handleAddChartClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent event bubbling
    setAddChartAnchorEl(event.currentTarget);
  };

  // Closes the add chart dropdown
  const handleAddChartClose = () => {
    setAddChartAnchorEl(null);
  };

  const IconHighlightBox = ({
    children,
    selected,
  }: {
    children: React.ReactNode;
    selected?: boolean;
  }) => {
    const hightlightStyle = {
      display: "flex",
      padding: "4px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      borderRadius: "8px",
      background: selected ? (theme.palette.mode === "light" ? "#E5E1D7" : "#5e5e5e") : "",
    };

    return <div style={hightlightStyle}>{children}</div>;
  };

  // vairable to get inactive charts and transform them into SubMenuOption[] type
  const inactiveChartOptions = React.useMemo((): SubMenuOption[] => {
    if (!charts) return [];
    return Object.entries(charts).map(([type, chart]) => {
      return { label: chart.title, value: type };
    });
  }, [charts]);

  return (
    <div style={{ display: "flex", gap: "12px" }}>
      <FilterButtonWrapper>
        <FilterButton selected={true} onClick={handleDateClick}>
          <IconHighlightBox selected={true}>
            <FilterListIcon sx={{ fontSize: "12px", color: theme.palette.text.primary }} />
          </IconHighlightBox>
          Date
        </FilterButton>
        <FilterButton
          selected={!showChartSelector}
          onClick={handleAddChartClick}
          disabled={!showChartSelector}>
          <IconHighlightBox selected={!showChartSelector}>
            <AddIcon sx={{ fontSize: "12px", color: theme.palette.text.primary }} />
          </IconHighlightBox>
          Add Chart
        </FilterButton>
      </FilterButtonWrapper>

      <DropdownSubmenu
        id="global-filter-submenu"
        anchorEl={dateAnchorEl}
        open={openDate}
        selected={period}
        onClose={handleDateClose}
        onSelection={handleDateSelection}
      />

      <DropdownSubmenu
        id="add-chart-submenu"
        anchorEl={addChartAnchorEl}
        open={openAddChart}
        options={inactiveChartOptions}
        onClose={handleAddChartClose}
        onSelection={handleAddChartSelection}
      />
    </div>
  );
};

export default GlobalFilter;
